import { RefObject, useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import Player from '../../player';
import { getPreviewHeight } from '../helpers';
import { uploadedImagesStyles } from 'components/uploaded-images/styles/styles';

const MAX_VIDEO_HEIGHT = 500;

interface PlayerWithCachedHeight {
  containerWidth: number;
  uploadId: string;
  url: string;
  containerRef: RefObject<HTMLDivElement>;
  previewUrl?: string;
  videoHeightCacheRef?: RefObject<Record<string, number>>;
}

export const PlayerWithCachedHeight = ({
  containerWidth,
  uploadId,
  previewUrl,
  url,
  containerRef,
  videoHeightCacheRef,
}: PlayerWithCachedHeight) => {
  const [height] = useState<number | null>(() => {
    const cached = uploadId && videoHeightCacheRef?.current?.[uploadId]
      ? videoHeightCacheRef.current[uploadId]
      : null;
    return cached ? Math.min(cached, MAX_VIDEO_HEIGHT) : null;
  });

  useEffect(() => {
    if (
      !containerWidth ||
      !uploadId ||
      !previewUrl ||
      !videoHeightCacheRef ||
      videoHeightCacheRef.current?.[uploadId]
    ) {
      return;
    }

    getPreviewHeight(previewUrl, containerWidth)
      .then((calculatedHeight) => {
        videoHeightCacheRef.current[uploadId] = Math.min(calculatedHeight, MAX_VIDEO_HEIGHT);
      })
      .catch(() => {
        // Preview image load failed — will use default height
      });
  }, [containerWidth, uploadId, previewUrl, videoHeightCacheRef]);

  return (
    <Grid
      ref={containerRef}
      sx={{
        ...uploadedImagesStyles.bigImageContainer,
        maxHeight: `${MAX_VIDEO_HEIGHT}px`,
        overflow: 'hidden',
        borderRadius: '12px',
        '& video': {
          width: '100%',
          maxHeight: `${MAX_VIDEO_HEIGHT}px`,
          objectFit: 'cover',
          borderRadius: '12px',
        },
      }}
      style={height ? { height: `${height}px` } : undefined}
    >
      {containerWidth ? <Player url={url} controls={true} /> : null}
    </Grid>
  );
};
